import {
  defer,
  EMPTY,
  from,
  identity,
  MonoTypeOperatorFunction,
  ObservableInput,
  ObservedValueOf,
  of,
  OperatorFunction,
} from 'rxjs';
import {delay, mergeMap, tap} from 'rxjs/operators';

/** Applies a trailing throttle on a per-value basis. */
export function distinctTrailingThrottleTime<T, K>(
  duration: number,
  keySelector: (value: T) => K,
): MonoTypeOperatorFunction<T>;
export function distinctTrailingThrottleTime<T>(duration: number): MonoTypeOperatorFunction<T>;
export function distinctTrailingThrottleTime<T>(
  duration: number,
  keySelector: (value: T) => any = identity,
): MonoTypeOperatorFunction<T> {
  return mergeMapDistinct((value) => of(value).pipe(delay(duration)), keySelector);
}

/** Like exhaustMap, but on a per-value basis. */
function mergeMapDistinct<T, K, O extends ObservableInput<any>>(
  project: (value: T) => O,
  keySelector: (value: T) => K,
): OperatorFunction<T, ObservedValueOf<O>>;
function mergeMapDistinct<T, O extends ObservableInput<any>>(
  project: (value: T) => O,
): OperatorFunction<T, ObservedValueOf<O>>;
function mergeMapDistinct<T, O extends ObservableInput<any>>(
  project: (value: T) => O,
  keySelector: (value: T) => any = identity,
): OperatorFunction<T, ObservedValueOf<O>> {
  // Use defer() so we create a new set for each subscriber.
  return (source$) =>
    defer(() => {
      const incompleteKeys = new Set<T>();

      return source$.pipe(
        mergeMap((value) => {
          const key = keySelector(value);

          // Check if an existing observable for this key has not completed yet.
          if (incompleteKeys.has(key)) {
            return EMPTY;
          }

          const input$ = project(value);

          // Remember this value.
          incompleteKeys.add(key);

          return from(input$).pipe(
            // Forget about this value when the observable completes.
            tap({
              complete: () => {
                incompleteKeys.delete(key);
              },
            }),
          );
        }),
      );
    });
}
